import React, { useCallback, useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore'
import { isNil, isEmpty, mergeLeft, reduce, assoc } from 'ramda'
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  makeStyles,
  TableContainer,
} from '@material-ui/core'

import CheckBox from 'app/componentV4/CheckBox'
import Pagination from 'app/componentV4/pagination/Pagination'

import BulkSelectedBox from 'app/componentV4/Table/BulkSelectedBox'
import EmptyDataTable from 'app/componentV4/Table/EmptyDataTable'
import LoaderDataTable from 'app/componentV4/Table/LoaderDataTable'

import {
  loaderRow,
  handleData,
  isCheckAll,
  loaderColumns,
  propsTableCell,
  isIndeterminate,
} from 'app/componentV4/Table/commons'
import InformationDialog from '../Dialog/InformationDialog'
import { SECONDARY } from 'app/constants/button'
import { useIntl } from 'react-intl'

const useStyles = makeStyles((theme) => ({
  wrapTable: {
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'center',
    flexDirection: 'column',
    height: 'calc(100vh - 247px)',
  },
  root: {
    borderRadius: '8px',
    maxHeight: 'calc(100vh - 230px)',
    '& .MuiTableCell-root': {
      padding: '1rem',
      backgroundColor: theme.colors.white,
      borderBottom: `1px solid ${theme.colors['primary-350']}`,
    },
    '& .MuiTableCell-stickyHeader': {
      fontSize: '14px',
      color: theme.colors.primary,
      position: 'sticky !important',
      backgroundColor: theme.colors.white,
      '& .MuiTypography-root': {
        display: 'flex',
        fontWeight: 'bolder',
        width: 'max-content',
      },
    },
    '& .MuiTableBody-root > .MuiTableRow-root': {
      height: '63px',
    },
    '& .MuiTableCell-body': {
      '& .MuiTypography-root': {
        fontSize: '16px',
        color: theme.colors['grey-400'],
      },
    },
  },
  hover: {
    cursor: 'pointer',
    '&.MuiTableRow-root.MuiTableRow-hover:hover > .MuiTableCell-root': {
      backgroundColor: theme.colors['primary-200'],
    },
    '&.MuiTableRow-root.MuiTableRow-hover:hover > .check-row': {
      backgroundColor: theme.colors['primary-200'],
    },
    '&.MuiTableRow-root.MuiTableRow-hover:hover > .action-row': {
      backgroundColor: theme.colors['primary-200'],
    },
  },
  hilight: {
    '&.MuiTableRow-root > .MuiTableCell-root': {
      backgroundColor: theme.colors['primary-200'],
    },
    '&.MuiTableRow-root > .check-row': {
      backgroundColor: theme.colors['primary-200'],
    },
    '&.MuiTableRow-root > .action-row': {
      backgroundColor: theme.colors['primary-200'],
    },
  },
  stickyCheckBox: {
    left: 0,
    width: '50px',
    position: 'sticky',
    background: 'white',
    transition: 'box-shadow 0.3s ease-in-out',
  },
  stickyAction: {
    right: 0,
    width: '50px',
    position: 'sticky',
    background: 'white',
    transition: 'box-shadow 0.3s ease-in-out',
  },
  withShadow: {
    boxShadow: '5px 0px 5px rgba(0, 0, 0, 0.1)',
  },
  withShadowRight: {
    '&:last-child': {
      boxShadow: '-2px 0px 5px rgba(0, 0, 0, 0.1)',
    },
  },
}))

const DataTable = ({
  isLoading,
  className,
  data = [],
  bulkAction,
  columns = [],
  pagination = {},
  activeRow = null,
  onFetch = () => {},
  sticky = 'checkBox',
  isCheckbox = false,
  onChange = () => {},
  isResetSelected = false,
  isResetAllSelected = false,
  setIsResetAllSelected = () => {},
  onSelectedRow = () => {},
  mode = null,
  onPerPageChange = () => {},
  onPageChange = () => {},
  customSelectedCount = null, // prop for support custom seleted count
  selectionLimit = null, // prop for max selectable rows (e.g., 3000)
}) => {
  const classes = useStyles()
  const tableRef = useRef(null)
  const { formatMessage } = useIntl()
  const [sorting, setSorting] = useState({})
  const [scrollLeft, setScrollLeft] = useState(false)
  const [selectedRows, setSelectedRows] = useState({})
  const [flatSelected, setFlatSelected] = useState([])
  const [hoveredItem, setHoveredItem] = useState(null)
  const [scrollRight, setScrollRight] = useState(false)
  const [currentSelected, setCurrentSelected] = useState([])
  const [openLimitDialog, setOpenLimitDialog] = useState(false)

  // Helper function to get total selected count across all pages
  const getTotalSelectedCount = useCallback((rows) => {
    return Object.values(rows).flat().length
  }, [])

  // Helper function to enforce selection limit
  const enforceSelectionLimit = useCallback(
    (newSelection, existingRows) => {
      if (!selectionLimit) return newSelection

      const totalSelected = getTotalSelectedCount(existingRows)
      const currentPageCount = newSelection.length
      const previousPageCount =
        existingRows[pagination.currentPage]?.length ?? 0
      const otherPagesCount = totalSelected - previousPageCount

      // Calculate how many more can be selected
      const allowedCount = Math.max(0, selectionLimit - otherPagesCount)

      // If new selection exceeds limit, truncate it
      if (currentPageCount > allowedCount) {
        return newSelection.slice(0, allowedCount)
      }

      return newSelection
    },
    [selectionLimit, pagination.currentPage, getTotalSelectedCount],
  )

  const handleSetSelected = useCallback(
    (value) => {
      const limitedValue = enforceSelectionLimit(value, selectedRows)
      setCurrentSelected(limitedValue)
      setSelectedRows((prev) =>
        mergeLeft({ [pagination.currentPage]: limitedValue }, prev),
      )
    },
    [pagination.currentPage, enforceSelectionLimit, selectedRows],
  )

  const handleRowSelect = (id) => {
    let currentData

    if (currentSelected.includes(id)) {
      currentData = currentSelected.filter((data) => data !== id)
    } else {
      // Check if adding this row would exceed the limit
      const totalSelected = getTotalSelectedCount(selectedRows)
      if (
        selectionLimit &&
        totalSelected >= selectionLimit &&
        !currentSelected.includes(id)
      ) {
        setOpenLimitDialog(true)
        return
      }
      currentData = [...currentSelected, id]
    }

    handleSetSelected(currentData)
  }

  const handleSelectAll = () => {
    let currentData
    if (currentSelected.length === data.length) {
      currentData = []
    } else {
      // Get all row IDs from current page
      const allRowIds = data.map(({ id }) => id)
      const totalSelected = getTotalSelectedCount(selectedRows)
      const otherPagesCount =
        totalSelected - (selectedRows[pagination.currentPage]?.length ?? 0)

      // Calculate how many more can be selected
      if (selectionLimit) {
        const allowedCount = Math.max(0, selectionLimit - otherPagesCount)

        // Show dialog if limit will be exceeded
        if (allowedCount < allRowIds.length) setOpenLimitDialog(true)

        currentData = allRowIds.slice(0, allowedCount)
      } else {
        currentData = allRowIds
      }
    }
    handleSetSelected(currentData)
  }

  const handlePages = (page) => {
    onPageChange(page)
    onFetch({ page: page, limit: Number(pagination.perPage) })
  }

  const handlePerpage = useCallback(
    (perPage) => {
      const persistedSelections = [
        ...flatSelected,
        ...Object.values(selectedRows).flat(),
      ]
      setFlatSelected(persistedSelections)
      setSelectedRows({})
      setCurrentSelected([])
      onFetch({ page: 1, limit: perPage })
      onPerPageChange(perPage)
    },
    [flatSelected, onFetch, selectedRows, onPerPageChange],
  )

  const handleSort = (name) => {
    setSelectedRows({})
    setCurrentSelected([])
    onFetch({
      page: pagination.currentPage,
      limit: Number(pagination.perPage),
      sort: `${name}:${sorting[name]}`,
    })
    setSorting((prev) =>
      mergeLeft({ [name]: prev[name] === 'ASC' ? 'DESC' : 'ASC' }, prev),
    )
  }

  const handleReduce = useCallback((data) => {
    return reduce((obj, item) => assoc(item.id, item, obj), {}, data)
  }, [])

  const handleStateFlat = useCallback(
    (data) => {
      let mappingData = []
      const reduceArray = handleReduce(data)

      setFlatSelected((prevs) =>
        prevs.filter((prev) => {
          if (isNil(reduceArray[prev])) return prev
          else mappingData.push(prev)
        }),
      )
      if (isNil(selectedRows[pagination.currentPage])) {
        handleSetSelected(mappingData)
      }
    },
    [handleReduce, handleSetSelected, pagination.currentPage, selectedRows],
  )

  const handleColumnSort = useCallback(() => {
    columns.forEach(({ sort, accessor }) => {
      if (sort)
        setSorting((prev) => {
          return { ...prev, [accessor]: 'ASC' }
        })
      else return
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOnChange = useCallback(() => {
    onChange(selectedRows, flatSelected)
  }, [selectedRows])

  const handleResetSelectedRow = useCallback(() => {
    setCurrentSelected([])
    const clone = { ...selectedRows }
    delete clone[pagination.currentPage]
    const restSelectedRows = clone
    setSelectedRows(restSelectedRows)
    setFlatSelected(Object.values(restSelectedRows).flat())
    handleStateFlat(data)
  }, [data, handleStateFlat, pagination.currentPage, selectedRows])

  const handleOnSelectedRow = (row, index) => {
    onSelectedRow(row)
  }

  const handleResetAllRows = useCallback(() => {
    setCurrentSelected([])
    setSelectedRows({})
    setFlatSelected([])
    handleStateFlat([])
  }, [handleStateFlat])

  useEffect(() => {
    if (isNil(activeRow)) setHoveredItem(null)
    else return
  }, [activeRow])

  useEffect(() => {
    handleOnChange()
  }, [handleOnChange])

  useEffect(() => {
    if (!isNil(selectedRows[pagination.currentPage])) {
      setCurrentSelected(selectedRows[pagination.currentPage])
    } else setCurrentSelected([])
  }, [pagination.currentPage, selectedRows])

  useEffect(() => {
    if (!isEmpty(flatSelected) && !isLoading) {
      handleStateFlat(data)
    }
    if (isLoading && !isNil(tableRef.current)) {
      tableRef.current.scrollTop = 0
      tableRef.current.scrollLeft = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading])

  useEffect(() => {
    handleColumnSort()
  }, [handleColumnSort])

  useEffect(() => {
    const tableElement = tableRef.current
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = tableElement

      setScrollLeft(scrollLeft > 0)
      setScrollRight(scrollLeft + clientWidth < scrollWidth)
    }

    tableElement.addEventListener('scroll', handleScroll)
    return () => {
      tableElement.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (isResetSelected) {
      handleResetSelectedRow()
    }
  }, [handleResetSelectedRow, isResetSelected])

  useEffect(() => {
    if (isResetAllSelected) {
      setIsResetAllSelected(false)
      handleResetAllRows()
    }
  }, [handleResetAllRows, isResetAllSelected])

  return (
    <div
      id="data-table"
      className={clsx(className, classes.wrapTable, 'data-table')}
    >
      {!isLoading && (
        <BulkSelectedBox
          selectedCount={
            customSelectedCount ?? // custom prop
            selectedRows[pagination.currentPage]?.length ?? // selected per page
            0
          }
          className="flex justify-between"
          mode={mode}
          pagination={pagination}
        >
          {bulkAction}
        </BulkSelectedBox>
      )}
      <TableContainer
        ref={tableRef}
        className={clsx(classes.root, isLoading && 'overflow-hidden')}
      >
        <Table stickyHeader aria-label="data-table">
          <TableHead>
            <TableRow>
              {isCheckbox && (
                <TableCell
                  className={clsx(
                    'z-99',
                    classes.stickyCheckBox,
                    sticky === 'checkBox' && scrollLeft && classes.withShadow,
                  )}
                >
                  <CheckBox
                    checked={
                      !isLoading &&
                      isCheckAll(data, currentSelected.length, isLoading)
                    }
                    indeterminate={
                      !isLoading &&
                      isIndeterminate(
                        selectedRows[pagination.currentPage]?.length,
                        data.length,
                      )
                    }
                    onChange={() => handleSelectAll()}
                    disabled={isEmpty(data)}
                  />
                </TableCell>
              )}
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  className={clsx(
                    sticky === column.accessor ? 'z-99' : 'z-50',
                    sticky === column.accessor &&
                      scrollLeft &&
                      classes.withShadow,
                    column.action && classes.stickyAction,
                    column.action && scrollRight && classes.withShadowRight,
                  )}
                  style={column.style}
                  {...propsTableCell(column)}
                >
                  <Typography
                    className={clsx(
                      'items-center',
                      column.disabled && 'opacity-25',
                    )}
                  >
                    {column.header}
                    {column.sort && (
                      <IconButton
                        aria-label="sort-table"
                        size="small"
                        onClick={() => handleSort(column.accessor)}
                        disabled={isLoading}
                      >
                        <UnfoldMoreIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableBody>
              <LoaderDataTable
                columns={columns}
                rowsNum={loaderRow(pagination)}
                isCheckbox={isCheckbox}
              />
            </TableBody>
          ) : (
            <TableBody>
              {isEmpty(data) ? (
                <TableRow>
                  <TableCell
                    colSpan={loaderColumns(isCheckbox, columns.length)}
                    className="border-0"
                  >
                    <EmptyDataTable />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      className={clsx(
                        classes.hover,
                        hoveredItem === rowIndex && classes.hilight,
                      )}
                      hover={true}
                    >
                      {isCheckbox && (
                        <TableCell
                          className={clsx(
                            'check-row',
                            'z-40',
                            classes.stickyCheckBox,
                            sticky === 'checkBox' &&
                              scrollLeft &&
                              classes.withShadow,
                          )}
                        >
                          <CheckBox
                            checked={currentSelected.includes(row.id)}
                            onChange={() => handleRowSelect(row.id)}
                          />
                        </TableCell>
                      )}
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={colIndex}
                          className={clsx(
                            'z-40',
                            sticky === column.accessor &&
                              scrollLeft &&
                              classes.withShadow,
                            column.action && 'action-row',
                            column.action && classes.stickyAction,
                            column.action &&
                              scrollRight &&
                              classes.withShadowRight,
                          )}
                          style={column.style}
                          onClick={() => {
                            column.action
                              ? setHoveredItem(rowIndex)
                              : handleOnSelectedRow(row, rowIndex)
                          }}
                          {...propsTableCell(column)}
                        >
                          {handleData(
                            column.render
                              ? column.render(row)
                              : row[column.accessor],
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isEmpty(data) && (
        <Pagination
          dataLength={Number(data.length)}
          totalCount={Number(pagination.total)}
          currentPage={Number(pagination.currentPage)}
          pageSize={Number(pagination.perPage)}
          lastPage={Number(pagination.lastPage)}
          onPageChange={(page) => handlePages(page)}
          onPerPageChange={(perPage) => handlePerpage(perPage)}
          disabled={isLoading}
        />
      )}

      <InformationDialog
        id="dialog-warning-table-limit"
        type={SECONDARY}
        open={openLimitDialog}
        description={formatMessage(
          {
            id: 'table.text.limit.reached.description',
          },
          { limitCount: selectionLimit },
        )}
        title={formatMessage({ id: 'text.limit.reached' })}
        onClose={() => setOpenLimitDialog(false)}
        closeText={formatMessage({ id: 'text.close' })}
      />
    </div>
  )
}

const columnsPropTypes = {
  header: PropTypes.any,
  accessor: PropTypes.string,
  action: PropTypes.bool,
  sort: PropTypes.bool,
}

const paginationPropTypes = {
  total: PropTypes.number,
  perPage: PropTypes.number,
  lastPage: PropTypes.number,
  currentPage: PropTypes.number,
}

DataTable.propTypes = {
  onFetch: PropTypes.func,
  sticky: PropTypes.string,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  bulkAction: PropTypes.node,
  isCheckbox: PropTypes.bool,
  activeRow: PropTypes.number,
  className: PropTypes.string,
  onSelectedRow: PropTypes.func,
  isResetSelected: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  pagination: PropTypes.shape(paginationPropTypes).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape(columnsPropTypes)).isRequired,
  mode: PropTypes.string,
  onPerPageChange: PropTypes.func,
  onPageChange: PropTypes.func,
  customSelectedCount: PropTypes.number,
  selectionLimit: PropTypes.number, // max selectable rows
  isResetAllSelected: PropTypes.bool,
  setIsResetAllSelected: PropTypes.func,
}

export default React.memo(DataTable)
